/**
 * Botón.
 *
 * FASE 1: ninguna acción escribe nada. Los botones del maquetado se reproducen porque forman
 * parte del diseño; los que necesitarían escritura se marcan con `phaseTwo`, que los deja
 * inertes (`aria-disabled`, sin `onClick`) y con un `title` que lo explica, pero conservando
 * el aspecto exacto: usar `disabled` los atenuaría y varias pantallas dejarían de ser
 * indistinguibles del maquetado.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'danger' | 'onBrand' | 'link' | 'dashedBlock';
type Size = 'default' | 'sm' | 'md';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  children?: ReactNode;
  variant?: Variant;
  size?: Size;
  /** Marca el botón como funcionalidad de la fase 2: deshabilitado y con explicación. */
  phaseTwo?: boolean;
  iconOnly?: boolean;
}

const VARIANT: Record<Variant, string> = {
  primary: styles.primary!,
  secondary: styles.secondary!,
  danger: styles.danger!,
  onBrand: styles.onBrand!,
  link: styles.link!,
  dashedBlock: styles.dashedBlock!,
};

const SIZE: Record<Size, string> = {
  default: '',
  sm: styles.sm!,
  md: styles.md!,
};

const PHASE_TWO_TITLE = 'Disponible en la fase 2 (escritura). Ver la hoja de ruta del README.';

export function Button({
  children,
  variant = 'primary',
  size = 'default',
  phaseTwo = false,
  iconOnly = false,
  type = 'button',
  onClick,
  ...rest
}: ButtonProps) {
  const isLink = variant === 'link';
  const classes = [
    isLink ? '' : styles.button,
    VARIANT[variant],
    isLink ? '' : SIZE[size],
    iconOnly ? styles.iconOnly : '',
    phaseTwo ? styles.phaseTwo : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      // `disabled` solo para los botones que de verdad no aplican (paginación al final).
      disabled={rest.disabled}
      aria-disabled={phaseTwo ? true : undefined}
      title={phaseTwo ? PHASE_TWO_TITLE : rest.title}
      onClick={phaseTwo ? undefined : onClick}
      {...rest}
    >
      {children}
    </button>
  );
}
